import { useGetElementDetail } from '@vindr/services/actions/element.action';
import { snakeToTitleCase } from '@vindr/utils/formatText';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';
import { Checkbox, Collapse, Form } from 'antd';
import isEqual from 'lodash/isEqual';
import { Fragment, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputComponentElement from './InputComponentElement';
import StructuredElement from './components/elements/structured';
import { SECTION_TYPE } from '@vindr/services/apis/templateStructuredReport';

const { Panel } = Collapse;

interface InputPanelProps {
  disabled: boolean;
  sections: StructuredElement[];
  onChangeValue: (value: any, element: any, preventHighlight?: boolean) => void;
}

function sortSections(sections: InputPanelProps['sections']): InputPanelProps['sections'] {
  const order = [
    SECTION_TYPE.CLINICAL_INFORMATION,
    SECTION_TYPE.TECHNIQUE,
    SECTION_TYPE.FINDINGS,
    SECTION_TYPE.COMPARISON,
    SECTION_TYPE.IMPRESSION,
  ];

  return sections.sort((a, b) => {
    const indexA = order.indexOf(a.name);
    const indexB = order.indexOf(b.name);
    return indexA - indexB;
  });
}

const InputPanel = memo(
  ({ disabled, sections, onChangeValue }: InputPanelProps) => {
    sections = sortSections(sections);
    return (
      <div className="input-panel">
        <Collapse accordion bordered={false} className="[&_.ant-collapse-header]:py-2">
          {sections?.map((section) => (
            <CollapseItem
              key={section?.id}
              disabled={disabled}
              isSection={true}
              section={section}
              onChange={onChangeValue}
            />
          ))}
        </Collapse>
      </div>
    );
  },
  (prev, next) => isEqual(prev.sections, next.sections) && isEqual(prev.disabled, next.disabled)
);
InputPanel.displayName = 'InputPanel';
export default InputPanel;

export interface CollapseItemProps extends Omit<InputPanelProps, 'sections' | 'onChangeValue'> {
  section: InputPanelProps['sections'][0];
  isSection: boolean;
  onChange: InputPanelProps['onChangeValue'];
}

const CollapseItem = ({ section, disabled, onChange, isSection, ...props }: CollapseItemProps) => {
  const { t } = useTranslation('Vindoc');
  const [isHidden, setIsHidden] = useState(section.is_hidden);
  const [refresh, setRefresh] = useRefreshComponent();

  useEffect(() => {
    if (section.is_hidden !== isHidden) {
      section.setIsHidden(isHidden);
      onChange(section.value, section);
      setRefresh();
    }
  }, [section, isHidden, setRefresh]);

  return (
    <Panel
      key={section.name}
      forceRender
      header={
        <div className="inline-flex items-center">
          <Checkbox
            checked={!isHidden}
            className="mr-2 mt-0.5"
            onChange={(e) => {
              setIsHidden(!e.target.checked);
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          />
          <span>{t(snakeToTitleCase(section.name))}</span>
        </div>
      }
      {...props}
    >
      <Fragment key={refresh}>
        {!isSection && (
          <Form.Item
            key={section.id}
            className="mb-3"
            label={section.label}
            name={section.name}
            tooltip={{ title: section.tooltip }}
          />
        )}

        <div className="ml-5">
          {section?.elements?.map((element) => {
            return <ElementLv1 key={element.id} {...{ element, onChange, disabled }} />;
          })}
        </div>
      </Fragment>
    </Panel>
  );
};

const ElementLv1 = memo(
  ({
    element,
    onChange,
    disabled,
  }: Omit<CollapseItemProps, 'section' | 'isSection'> & {
    element: CollapseItemProps['section']['elements'][0];
  }) => {
    const [refresh, setRefresh] = useRefreshComponent();
    const { data, isFetching } = useGetElementDetail(
      { id: element?.id },
      {
        enabled:
          element?.level <= 1 ||
          (!element?.elements?.length && !!element?.linked_element_ids?.length),
      }
    );
    const newElements = useMemo(() => data?.elements, [data]);

    useLayoutEffect(() => {
      if (!newElements || newElements.length === 0 || isFetching) return;
      const mergeElements = data.elements.map((x, index) => ({ ...x, ...element.elements[index] }));
      element.setElements(mergeElements);
      setRefresh();
    }, [data?.elements, element, isFetching, newElements, setRefresh]);

    if (!element) return null;

    return (
      <InputComponentElement
        key={refresh}
        disabled={disabled}
        element={element}
        onChange={onChange}
      />
    );
  },
  (prev, next) =>
    (isEqual(prev.element, next.element) ||
      JSON.stringify(prev.element) === JSON.stringify(next.element)) &&
    isEqual(prev.disabled, next.disabled)
);
ElementLv1.displayName = 'ElementLv1';
CollapseItem.displayName = 'CollapseItem';
